import axios from "axios";
import _ from "lodash";
import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { jsonKeyMapping, templateActionEnum } from "../../../Config/Constants";
import FormDataContext from "../../../Contexts/FormData.context";
import TemplateDataContext from "../../../Contexts/TemplateData.context";
import { logger } from "../../../Middleware/Logger";
import { TemplateObject } from "../../../Models/TemplateObject";
// import { processFormRequest } from '../../../Services/TemplateServices'
import ErrorMessage from "../../../Util/ErrorMessage";
import FormBuilder from "../../FormBuilder/FormBuilder";
import ButtonComponent from "../../Shared/ButtonComponent";
import FaqComponent from "../../Shared/FaqComponent";
import PreviewComponent from "../../Shared/PreviewComponent";
import StepBoxLeft from "../../StepContent/StepContentHeader/StepBoxLeft";
import ReviewSummary from "./ReviewSummary";
import Spinner from "react-bootstrap/Spinner";
import { PathHelper } from "../../../Util/PathHelper";
import { APP_NAVIGATION } from "../../../Util/Constants";
import {
  analyzeStepQuestionType,
  QuestionTypeResult,
} from "../../../Util/QuestionTypeIdentifier";
import "./MainForm.css";
/*
  LAYOUT CHANGES SUMMARY:
  1. Removed preview column functionality from layout (kept imports for mobile/other uses)
  2. Updated main container to use col-lg-12 for full width since no preview column
  3. Added form-content-container class: 872px × 856px with 48px/96px padding
  4. Added form-fields-container class: 480px × 856px centered within content area
  5. Moved navigation buttons inside form-fields-container with 48px top spacing
  6. All form content (titles, fields, buttons) now constrained to 480px width
  7. Layout respects expandable sidebar spacing via CSS padding adjustments
*/

type Props = {
  nextBtn: React.MouseEventHandler<HTMLButtonElement>;
  backBtn: React.MouseEventHandler<HTMLButtonElement>;
  formSubmit: React.MouseEventHandler<HTMLButtonElement>;
  stepDetails: TemplateObject;
  isHideSteps: string[];
  isLoading: boolean;
  templatePrev?: React.RefObject<HTMLDivElement>;
};

const MainForm = ({
  nextBtn,
  // Override backBtn to always go to previous real form step
  // (see below for new handler)
  formSubmit,
  stepDetails,
  isHideSteps,
  isLoading,
  templatePrev,
}: Props) => {
  const { state, dispatch } = useContext(TemplateDataContext);
  const { state: FormState } = useContext(FormDataContext);
  const { formData } = FormState;
  const { templates, activeTemplateSteps } = state;
  const REVIEW_SLUG = "review-submit";
  let { template, stepName, state: templateState, templateType } = useParams();
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [hasSubmit, setHasSubmit] = useState(false);
  const [currentQuestionType, setCurrentQuestionType] = useState<
    "address" | "case" | "person" | "misc"
  >("misc");
  const [currentQuestionIcon, setCurrentQuestionIcon] = useState<string>(
    process.env.PUBLIC_URL + "/static/images/icon/misc.svg"
  );

  const stepLength: any = stepDetails;
  const activeStepLength =
    Object.keys(stepLength).length > 0 &&
    stepLength.formGroup &&
    Array.isArray(stepLength.formGroup)
      ? stepLength.formGroup.length
      : 0;
  let currentStepNumber = activeTemplateSteps.findIndex(
    (x) => x[jsonKeyMapping.SLUGIFY] === stepName
  );
  if (activeTemplateSteps.length > 0) {
    stepName =
      currentStepNumber === -1 ? activeTemplateSteps[0].slugify : stepName;
  }
  const frstStep =
    Object.keys(stepLength).length > 0 &&
    stepLength.formGroup &&
    Object.keys(stepLength.formGroup).length === 0
      ? stepLength.stepname.toLowerCase()
      : "";
  const navigate = useNavigate();
  const RedirectPath = PathHelper(APP_NAVIGATION.TEMP);
  // const formSubmit = async () => {
  //     // const cancelToken = axios.CancelToken.source()
  //     // const config = { cancelToken: cancelToken.token }
  //     try {
  //         const submitFormData: TemplateObject = {
  //             template,
  //             templateName: templateType,
  //             state: templateState || '',
  //             formData: {}
  //         }
  //         for (const key in formData) {
  //             if (!key.match('meta-data-')) {
  //                 submitFormData.formData[key] = formData[key]
  //             }
  //         }
  //         // TO DO ::: Redirect
  //         navigate(RedirectPath.next);
  //         // window.location.href = '/app/template/sign-up';
  //         // const { data } = await processFormRequest({ formData: submitFormData, config })
  //         // const { redirectUrl } = data
  //         // window.location.href = redirectUrl
  //     } catch (error) {
  //         if (!axios.isCancel(error)) {
  //             logger.error('🚀 ~ file: useEffect.tsx ~ line 47 ~ changeLayout ~ error', error)
  //             dispatch({ type: templateActionEnum.SET_ERROR, payload: { show: true, message: ErrorMessage.PROCESSING_FORM } })
  //         }
  //     }
  // }

  useEffect(() => {
    // if that step provide hasNext or previous then go with it
    if (
      stepDetails[jsonKeyMapping.HAS_PREVIOUS] !== undefined ||
      stepDetails[jsonKeyMapping.HAS_NEXT] !== undefined ||
      stepDetails[jsonKeyMapping.HAS_SUBMIT] !== undefined
    ) {
      // If the template explicitly sets navigation flags, respect them.
      setHasNext(stepDetails[jsonKeyMapping.HAS_NEXT] || false);
      setHasPrevious(stepDetails[jsonKeyMapping.HAS_PREVIOUS] || false);
      setHasSubmit(stepDetails[jsonKeyMapping.HAS_SUBMIT] || false);
    } else if (
      templates &&
      template &&
      stepName &&
      activeTemplateSteps.length > 0
    ) {
      // Use actual form steps for navigation logic instead of all steps
      const actualSteps = getActualFormSteps(activeTemplateSteps);
      let indexOfThisStep = actualSteps.findIndex(
        (step) => step[jsonKeyMapping.SLUGIFY] === stepName
      );
      indexOfThisStep = indexOfThisStep === -1 ? 0 : indexOfThisStep;

      // if first actual form step then next = true, prev = false , submit = false
      if (indexOfThisStep === 0) {
        setHasNext(true);
        setHasPrevious(false);
        setHasSubmit(false);
      } else if (indexOfThisStep === actualSteps.length - 1) {
        // if last actual form step then show Next (so user can go to the Review step)
        setHasNext(true);
        setHasPrevious(true);
        setHasSubmit(false);
      } else if (indexOfThisStep > 0 && indexOfThisStep < actualSteps.length) {
        // if step is not last and not first among actual form steps then set next and previous visible
        setHasNext(true);
        setHasPrevious(true);
        setHasSubmit(false);
      }
    }

    return () => {};
  }, [stepDetails, activeTemplateSteps]);

  // Analyze form group to determine question type and update icon
  useEffect(() => {
    if (
      stepDetails[jsonKeyMapping.FORM_GROUP] &&
      stepDetails[jsonKeyMapping.FORM_GROUP].length > 0
    ) {
      const analysis = analyzeStepQuestionType(stepDetails);
      setCurrentQuestionType(analysis.category);
      setCurrentQuestionIcon(analysis.icon);

      // Debug logging
      console.log("Step Question Type Analysis:", {
        stepName,
        stepTitle: stepDetails[jsonKeyMapping.SLUG],
        formGroup: stepDetails[jsonKeyMapping.FORM_GROUP],
        analysis,
        iconPath: analysis.icon,
      });
    } else {
      setCurrentQuestionType("misc");
      setCurrentQuestionIcon(
        process.env.PUBLIC_URL + "/static/images/icon/misc.svg"
      );
    }
  }, [stepDetails, stepName]);
  const step: string = stepName as string;

  // Helper function to filter out introductory/intermediary screens
  const getActualFormSteps = (steps: TemplateObject[]) => {
    return steps.filter((step) => {
      const hasFormGroup =
        step[jsonKeyMapping.FORM_GROUP] &&
        Array.isArray(step[jsonKeyMapping.FORM_GROUP]) &&
        step[jsonKeyMapping.FORM_GROUP].length > 0;
      return hasFormGroup;
    });
  };

  // Get only the actual form steps for counting
  const actualFormSteps = getActualFormSteps(activeTemplateSteps);

  // Track active field index for current form step (represents filled field count)
  const [activeFieldIndex, setActiveFieldIndex] = useState(0);
  // Baseline: number of fields already filled when entering this step (so progress starts visually at 0)
  const [baselineFilledCount, setBaselineFilledCount] = useState(0);
  const [totalFieldsInCurrentForm, setTotalFieldsInCurrentForm] = useState(
    (stepDetails[jsonKeyMapping.FORM_GROUP] || []).length
  );
  /* Single Field Mode: always true per user request (can be toggled later via env/prop) */
  const SINGLE_FIELD_MODE = true;
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0); // which field in this step is currently visible
  const [isFieldListOpen, setIsFieldListOpen] = useState(false);
  const fieldListRef = useRef<HTMLDivElement | null>(null);
  const fieldListToggleRef = useRef<HTMLButtonElement | null>(null);
  const [mobileDropdownTop, setMobileDropdownTop] = useState<number | null>(
    null
  );
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth <= 600 : false
  );

  // Refs for scrollable fields area and the action bar so we can compute available height
  const formContentRef = useRef<HTMLDivElement | null>(null);
  const fieldsScrollRef = useRef<HTMLDivElement | null>(null);
  const actionsRef = useRef<HTMLDivElement | null>(null);

  // Listen for viewport size changes to toggle mobile/desktop behavior
  useEffect(() => {
    const handleResize = () => {
      const mobileNow = window.innerWidth <= 600;
      setIsMobile(mobileNow);
      if (!mobileNow) {
        // Leaving mobile: clear mobile-specific inline positioning so desktop width styling applies seamlessly
        setMobileDropdownTop(null);
      } else if (mobileNow && isFieldListOpen) {
        // Entering mobile with list open: recompute top
        if (fieldListToggleRef.current) {
          const rect = fieldListToggleRef.current.getBoundingClientRect();
          setMobileDropdownTop(rect.bottom + 8);
        }
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isFieldListOpen]);

  // compute and apply max-height to fields scrollable area so only inputs scroll on mobile
  useLayoutEffect(() => {
    const computeFieldsMaxHeight = () => {
      try {
        if (
          !fieldsScrollRef.current ||
          !formContentRef.current ||
          !actionsRef.current
        )
          return;
        const containerRect = formContentRef.current.getBoundingClientRect();
        const topOffset = Math.max(0, containerRect.top);
        const actionsHeight = actionsRef.current.offsetHeight || 0;
        const paddingBuffer = 20; // safety buffer
        const available =
          window.innerHeight - topOffset - actionsHeight - paddingBuffer;
        const minHeight = 120;
        const maxHeight = Math.max(minHeight, available);
        fieldsScrollRef.current.style.maxHeight = `${maxHeight}px`;
        fieldsScrollRef.current.style.overflow = "auto";
        (fieldsScrollRef.current.style as any).webkitOverflowScrolling =
          "touch";
      } catch (err) {
        // ignore
      }
    };

    computeFieldsMaxHeight();
    const onResize = () => computeFieldsMaxHeight();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    const bodyObserver = new MutationObserver(() => computeFieldsMaxHeight());
    bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class", "style", "data-open"],
    });

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      bodyObserver.disconnect();
    };
  }, []);

  // Close dropdown on outside click or ESC
  useEffect(() => {
    if (!isFieldListOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        fieldListRef.current &&
        !fieldListRef.current.contains(e.target as Node)
      ) {
        setIsFieldListOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsFieldListOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isFieldListOpen]);

  const toggleFieldList = () => {
    setIsFieldListOpen((prev) => {
      const next = !prev;
      if (next && typeof window !== "undefined" && isMobile) {
        requestAnimationFrame(() => {
          if (fieldListToggleRef.current) {
            const rect = fieldListToggleRef.current.getBoundingClientRect();
            setMobileDropdownTop(rect.bottom + 8); // 8px gap below toggle
          }
        });
      }
      return next;
    });
  };

  const jumpToField = (idx: number) => {
    if (!SINGLE_FIELD_MODE) return;
    setCurrentFieldIndex(idx);
    setIsFieldListOpen(false);
  };

  // In single-field mode, ensure back button appears when not on first field even on first step
  useEffect(() => {
    if (!SINGLE_FIELD_MODE) return;
    if (currentFieldIndex > 0) {
      setHasPrevious(true);
      return;
    }
    const actualStepsLocal = getActualFormSteps(activeTemplateSteps);
    const idx = actualStepsLocal.findIndex(
      (s) => s[jsonKeyMapping.SLUGIFY] === stepName
    );
    if (idx <= 0) {
      setHasPrevious(false);
    }
  }, [currentFieldIndex, SINGLE_FIELD_MODE, activeTemplateSteps, stepName]);

  // Ref to restore a specific field index after navigating to a previous step
  const restoreFieldIndexRef = useRef<number | null>(null);

  // Reset or restore currentFieldIndex whenever step changes or form group length changes
  useEffect(() => {
    if (!SINGLE_FIELD_MODE) return;
    const fgLen = (stepDetails[jsonKeyMapping.FORM_GROUP] || []).length;
    if (restoreFieldIndexRef.current !== null) {
      const idx = restoreFieldIndexRef.current;
      restoreFieldIndexRef.current = null;
      setCurrentFieldIndex(idx < fgLen ? idx : fgLen - 1);
    } else {
      setCurrentFieldIndex(0);
    }
  }, [
    stepName,
    stepDetails[jsonKeyMapping.FORM_GROUP]?.length,
    SINGLE_FIELD_MODE,
  ]);

  // Apply body class for styling hooks & dynamic sidebar differentiation
  useEffect(() => {
    if (SINGLE_FIELD_MODE) {
      document.body.classList.add("single-field-mode");
      return () => document.body.classList.remove("single-field-mode");
    }
  }, [SINGLE_FIELD_MODE]);

  // Dynamic per-field help: derive from field subtooltip > faq (matched by headline) > step tooltip/description
  useEffect(() => {
    if (!SINGLE_FIELD_MODE) return;
    const fg = stepDetails[jsonKeyMapping.FORM_GROUP] || [];
    if (!Array.isArray(fg) || fg.length === 0) return;
    if (currentFieldIndex < 0 || currentFieldIndex >= fg.length) return;
    const field = fg[currentFieldIndex];

    let helpTitle = "";
    let helpDescription = "";

    // 1. Subtooltip (HTML) priority
    if (
      field?.subtooltip &&
      typeof field.subtooltip === "string" &&
      field.subtooltip.trim()
    ) {
      // Extract headline inside <span class='headline'> if present
      const titleMatch = field.subtooltip.match(
        /<span class=['"]headline['"]>(.*?)<\/span>/
      );
      if (titleMatch) helpTitle = titleMatch[1];
      // Strip HTML for description, remove headline duplication
      const textOnly = field.subtooltip.replace(/<[^>]*>/g, "").trim();
      if (textOnly) {
        helpDescription = textOnly.startsWith(helpTitle)
          ? textOnly.substring(helpTitle.length).trim()
          : textOnly;
      }
    }

    // 2. Field level tooltip fallback if no subtooltip based content
    if (!helpDescription && field?.tooltip) {
      helpTitle = helpTitle || field.label || "Help";
      helpDescription = field.tooltip.replace(/<[^>]*>/g, "").trim();
    }

    // 3. Match FAQ by header containing field label (if still empty)
    if (
      !helpDescription &&
      stepDetails[jsonKeyMapping.FAQ] &&
      Array.isArray(stepDetails[jsonKeyMapping.FAQ])
    ) {
      const label = field.label || field.id;
      if (label) {
        const match = stepDetails[jsonKeyMapping.FAQ].find((f: any) =>
          (f.header || "").toLowerCase().includes(String(label).toLowerCase())
        );
        if (match) {
          helpTitle = helpTitle || match.header || "FAQ";
          helpDescription = (match.Text || match.text || "")
            .replace(/<[^>]*>/g, "")
            .trim();
        }
      }
    }

    // 4. Step level description fallback
    if (!helpDescription && stepDetails[jsonKeyMapping.DESCRIPTION]) {
      helpTitle =
        helpTitle || stepDetails[jsonKeyMapping.SLUG] || "Information";
      helpDescription = stepDetails[jsonKeyMapping.DESCRIPTION];
    }

    // ALWAYS show the question label as the title (per user request) while keeping the contextual help as description.
    const labelFallback = field.label || field.id || "";
    if (labelFallback) {
      helpTitle = labelFallback; // override any previous headline-based title to ensure question label appears
    }
    // If description was empty but we had previously parsed a headline differing from label, we could reuse it.
    if (
      !helpDescription &&
      field?.subtooltip &&
      typeof field.subtooltip === "string"
    ) {
      const stripped = field.subtooltip.replace(/<[^>]*>/g, "").trim();
      if (
        stripped &&
        stripped.toLowerCase() !== String(labelFallback).toLowerCase()
      ) {
        helpDescription = stripped;
      }
    }

    // Update body dataset so ExpandableSidebar MutationObserver picks changes
    const body = document.body as any;
    if (helpTitle) body.dataset.tooltipDefaultTitle = helpTitle;
    if (helpDescription)
      body.dataset.tooltipDefaultDescription = helpDescription;
    if (!helpTitle && !helpDescription) {
      delete body.dataset.tooltipDefaultTitle;
      delete body.dataset.tooltipDefaultDescription;
    }
  }, [SINGLE_FIELD_MODE, currentFieldIndex, stepDetails]);

  // When form group (or step) changes, recompute baseline and total fields
  useEffect(() => {
    const fg = stepDetails[jsonKeyMapping.FORM_GROUP] || [];
    setTotalFieldsInCurrentForm(fg.length);
    // Count previously filled values for fields in this step
    let filled = 0;
    fg.forEach((el: any) => {
      let id = el.id || el.label || "";
      if (typeof id === "string") id = id.replaceAll(" ", "");
      if (formData && formData[id] !== undefined && formData[id] !== "")
        filled++;
    });
    setBaselineFilledCount(filled);
    // Initialize active index to baseline so displayed progress (active - baseline) starts at 0
    setActiveFieldIndex(filled);
    // eslint-disable-next-line
  }, [stepDetails[jsonKeyMapping.FORM_GROUP], stepName]);

  // Auto-skip introductory/intermediary screens
  useEffect(() => {
    if (activeTemplateSteps.length > 0 && stepName) {
      const currentStep = activeTemplateSteps.find(
        (step) => step[jsonKeyMapping.SLUGIFY] === stepName
      );

      if (currentStep) {
        // Check if this is an introductory/intermediary screen (empty formGroup)
        const isIntroductoryScreen =
          !currentStep[jsonKeyMapping.FORM_GROUP] ||
          !Array.isArray(currentStep[jsonKeyMapping.FORM_GROUP]) ||
          currentStep[jsonKeyMapping.FORM_GROUP].length === 0;

        if (isIntroductoryScreen) {
          // Find the next actual form step
          const currentStepIndex = activeTemplateSteps.findIndex(
            (step) => step[jsonKeyMapping.SLUGIFY] === stepName
          );

          const nextFormStep = activeTemplateSteps
            .slice(currentStepIndex + 1)
            .find(
              (step) =>
                step[jsonKeyMapping.FORM_GROUP] &&
                Array.isArray(step[jsonKeyMapping.FORM_GROUP]) &&
                step[jsonKeyMapping.FORM_GROUP].length > 0
            );

          if (nextFormStep) {
            // Redirect to the next actual form step
            const currentPath = window.location.pathname;
            const newPath = currentPath.replace(
              `/${stepName}`,
              `/${nextFormStep[jsonKeyMapping.SLUGIFY]}`
            );
            navigate(newPath, { replace: true });
            return;
          }
        }
      }
    }
  }, [stepName, activeTemplateSteps, navigate]);

  // Mobile navigation bar state management for dropdown behavior
  useEffect(() => {
    // Only run on mobile screens (< 600px) and only on form pages
    const handleDropdownState = () => {
      const isMobileScreen = window.innerWidth < 600;
      const navigationBar = document.getElementById('navigationBar');
      
      // Exit early if not mobile or navigationBar doesn't exist
      if (!isMobileScreen || !navigationBar) return;
      
      // Check if we're on a form page (has form fields)
      const isFormPage = stepDetails[jsonKeyMapping.FORM_GROUP] && 
                        stepDetails[jsonKeyMapping.FORM_GROUP].length > 0;
      
      if (!isFormPage) return;
      
      // Check if either dropdown is open
      const isStepsDropdownOpen = document.body.classList.contains('steps-dropdown-open');
      const isTipsDropdownOpen = document.body.classList.contains('tips-dropdown-open');
      
      const anyDropdownOpen = isStepsDropdownOpen || isTipsDropdownOpen;
      
      // Apply appropriate class based on dropdown states
      if (anyDropdownOpen) {
        navigationBar.classList.remove('form-navigation-bar-wrapper');
        navigationBar.classList.add('form-navigation-bar-wrapper-expandedIsTrue');
      } else {
        navigationBar.classList.remove('form-navigation-bar-wrapper-expandedIsTrue');
        navigationBar.classList.add('form-navigation-bar-wrapper');
      }
    };
    
    // Handle click events for the dropdown buttons
    const handleButtonClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const button = target.closest('#menuButton, #toggleIconButton');
      
      if (button && (button.id === 'menuButton' || button.id === 'toggleIconButton')) {
        // Use setTimeout to allow the body classes to be updated first
        setTimeout(handleDropdownState, 10);
      }
    };
    
    // Handle resize events to check mobile state
    const handleResize = () => {
      handleDropdownState();
    };
    
    // Add event listeners
    document.addEventListener('click', handleButtonClick);
    window.addEventListener('resize', handleResize);
    
    // Also watch for body class changes (dropdown state changes)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          handleDropdownState();
        }
      });
    });
    
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    // Initial check
    handleDropdownState();
    
    // Cleanup
    return () => {
      document.removeEventListener('click', handleButtonClick);
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
      
      // Clean up classes on unmount
      const navigationBar = document.getElementById('navigationBar');
      if (navigationBar) {
        navigationBar.classList.remove('form-navigation-bar-wrapper-expandedIsTrue');
        navigationBar.classList.add('form-navigation-bar-wrapper');
      }
    };
  }, [stepDetails]); // Re-run when step details change

  // Custom back button handler: always go to previous real form step
  const handleBack = () => {
    if (SINGLE_FIELD_MODE) {
      // First attempt: move to previous field within the same step
      if (currentFieldIndex > 0) {
        setCurrentFieldIndex((i) => i - 1);
        return;
      }
    }
    // Step-level back if first field
    const currentStepIndex = actualFormSteps.findIndex(
      (step) => step[jsonKeyMapping.SLUGIFY] === stepName
    );
    if (currentStepIndex > 0) {
      const prevStep = actualFormSteps[currentStepIndex - 1];
      if (prevStep) {
        const prevFG = (prevStep[jsonKeyMapping.FORM_GROUP] || []) as any[];
        const lastIndex = prevFG.length > 0 ? prevFG.length - 1 : 0;
        restoreFieldIndexRef.current = lastIndex;
        const currentPath = window.location.pathname;
        const newPath = currentPath.replace(
          `/${stepName}`,
          `/${prevStep[jsonKeyMapping.SLUGIFY]}`
        );
        navigate(newPath);
      }
    }
  };

  // Submit button click behavior: if we're on the review page, call the real submit handler;
  // otherwise navigate to the review page so user can review before final submit.
  const handleSubmitClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Only perform the final submission action here. Navigating to the review step is done
    // by clicking Next on the last JSON form step (which will route to the injected review step).
    if (typeof formSubmit === "function") {
      (formSubmit as any)(e);
    }
  };


  // Single-field aware next handler wrapper
  const handleNextFieldOrStep = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (
      SINGLE_FIELD_MODE &&
      stepDetails[jsonKeyMapping.FORM_GROUP] &&
      stepDetails[jsonKeyMapping.FORM_GROUP].length > 0
    ) {
      const fgLen = stepDetails[jsonKeyMapping.FORM_GROUP].length;
      if (currentFieldIndex < fgLen - 1) {
        setCurrentFieldIndex((i) => i + 1);
        return;
      }
    }
    // If last field or not in single mode, proceed with existing next step logic
    if (typeof nextBtn === "function") {
      (nextBtn as any)(e);
    }
  };

  return (
    <>
      <div
        className={`col-lg-12 form-content-container mx-auto ${
          isHideSteps && !isHideSteps.includes(step) && "offsetmargin"
        }`}
      >
        {/* 
          LAYOUT CHANGE: Updated to use col-lg-12 for full width since preview column was removed.
          Applied form-content-container class to establish 872px × 856px content area with 48px/96px padding.
        */}
        <div className="scrollable-div leftsidebar h-75" ref={formContentRef}>
          {/* TEMPLATE BODY */}
          <div
            // className="card-body bg-grey pt-0 h-75"
            className={
              stepDetails[jsonKeyMapping.FORM_GROUP] &&
              stepDetails[jsonKeyMapping.FORM_GROUP].length > 0
                ? "h-75"
                : "" + " card-body bg-grey"
            }
          >
            <div
              className={`form-fields-container ${
                stepName === REVIEW_SLUG ? "review-full-width" : ""
              }`}
            >
              {/* Form Header - positioned inside container for 480px width constraint */}
              {/* Only show header with step counter for actual form steps */}
              {stepDetails[jsonKeyMapping.FORM_GROUP] &&
                stepDetails[jsonKeyMapping.FORM_GROUP].length > 0 && (
                  <div className="form-header">
                    <div className="form-header-icon-container">
                      <img
                        src={currentQuestionIcon}
                        alt={`${currentQuestionType} icon`}
                        className="form-header-icon"
                        width="18"
                        height="25"
                      />
                    </div>
                    <div className="form-header-step-info">
                      {(() => {
                        const total = totalFieldsInCurrentForm || 0;
                        const currentIdx = SINGLE_FIELD_MODE
                          ? Math.min(currentFieldIndex, Math.max(total - 1, 0))
                          : Math.min(
                              activeFieldIndex - baselineFilledCount,
                              Math.max(total - 1, 0)
                            );
                        const displayCurrent = total === 0 ? 0 : currentIdx + 1;
                        return (
                          <>
                            <span>Step&nbsp;</span>
                            <strong className="step-number">
                              {displayCurrent}
                            </strong>
                            <span>&nbsp;of&nbsp;</span>
                            <strong className="step-number">{total}</strong>
                            {total > 0 && (
                              <button
                                type="button"
                                aria-haspopup="listbox"
                                aria-expanded={isFieldListOpen}
                                onClick={toggleFieldList}
                                className={`field-list-toggle ${
                                  isFieldListOpen ? "is-open" : ""
                                }`}
                                ref={fieldListToggleRef}
                                title={
                                  isFieldListOpen
                                    ? "Hide fields"
                                    : "Show fields"
                                }
                              >
                                <span
                                  className="field-list-toggle__chevron"
                                  aria-hidden="true"
                                >
                                  <img
                                    src={
                                      process.env.PUBLIC_URL +
                                      "/static/images/icon/chevron-down.svg"
                                    }
                                    width={14}
                                    height={14}
                                    alt="toggle fields"
                                    className="chevron-img"
                                  />
                                </span>
                              </button>
                            )}
                          </>
                        );
                      })()}
                      {isFieldListOpen && (
                        <div
                          ref={fieldListRef}
                          className={`field-dropdown-list ${
                            isMobile ? "field-dropdown-list--mobile" : ""
                          }`}
                          role="listbox"
                          aria-label="Fields in this step"
                          style={
                            isMobile && mobileDropdownTop !== null
                              ? {
                                  position: "fixed",
                                  top: mobileDropdownTop,
                                  left: 15,
                                  right: 15,
                                  width: "calc(100vw - 30px)",
                                  maxWidth: "none",
                                  minWidth: 0,
                                  zIndex: 2000,
                                }
                              : undefined
                          }
                        >
                          <ol className="field-dropdown-list__items">
                            {(stepDetails[jsonKeyMapping.FORM_GROUP] || []).map(
                              (f: any, idx: number) => {
                                const idKey = f.id || f.label || `field-${idx}`;
                                let normalizedId =
                                  typeof idKey === "string"
                                    ? idKey.replace(/\s+/g, "")
                                    : idKey;
                                const filled =
                                  formData &&
                                  formData[normalizedId] !== undefined &&
                                  formData[normalizedId] !== "";
                                const isCurrent = idx === currentFieldIndex;
                                const statusClass = isCurrent
                                  ? "field-current"
                                  : filled
                                  ? "field-completed"
                                  : "field-pending";
                                return (
                                  <li
                                    key={idx}
                                    role="option"
                                    aria-selected={isCurrent}
                                    onClick={() => jumpToField(idx)}
                                    className={`field-list-item ${statusClass}`}
                                  >
                                    <span className="field-list-item__label">
                                      {f.label || f.id || `Field ${idx + 1}`}
                                    </span>
                                  </li>
                                );
                              }
                            )}
                          </ol>
                        </div>
                      )}
                    </div>
                  </div>
                )}

              {/* 
                LAYOUT CHANGE: Added form-fields-container wrapper to constrain all form content 
                (titles, form fields, buttons) to 480px × 856px centered within the 872px content area.
                This includes both conditional title layouts and form content.
              */}
              {stepDetails[jsonKeyMapping.FORM_GROUP] &&
              stepDetails[jsonKeyMapping.FORM_GROUP].length > 0 ? (
                <div className="bg-grey pb-20">
                  {/* Suppress step title for the injected review step */}
                  {stepName !== REVIEW_SLUG && (
                    <>
                      <p
                        className="font-22 blue-color"
                        style={{
                          marginBottom: stepDetails[jsonKeyMapping.DESCRIPTION]
                            ? "8px"
                            : "0",
                        }}
                      >
                        {stepDetails[jsonKeyMapping.SLUG]}
                      </p>
                      {/* Newly added: inline description directly under step title to integrate into flow */}
                      {stepDetails[jsonKeyMapping.DESCRIPTION] && (
                        <p
                          className="subtitle step-description"
                          style={{ marginTop: 0, marginBottom: "0px" }}
                        >
                          {stepDetails[jsonKeyMapping.DESCRIPTION]}
                        </p>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <>
                  <div className="col-md-12 great-page">
                    {frstStep === "start" ? (
                      <>
                        {" "}
                        <div className="blue-color text-center mb-2">
                          <h4 className="lg-font blue-color">
                            {stepDetails[jsonKeyMapping.SLUG]}
                          </h4>
                          <div className="mt-2 font-16 ">
                            {/* {stepDetails[jsonKeyMapping.STEPS_BOX_LEFT] && (

                                                          <img src={process.env.PUBLIC_URL + '/static/images/box-icon-first.png'} className="m-2" />
                                                      )} */}
                            <p className="text-center mb-20">
                              {stepDetails[jsonKeyMapping.DESCRIPTION]
                                ? stepDetails[jsonKeyMapping.DESCRIPTION]
                                : ""}
                            </p>
                            <div className="text-center">
                              <h5 className="fw-600 body-font2">
                                {stepDetails[jsonKeyMapping.TITLE_SECOND]
                                  ? stepDetails[jsonKeyMapping.TITLE_SECOND]
                                  : ""}
                              </h5>
                            </div>
                          </div>
                          <p>
                            {stepDetails[jsonKeyMapping.STEPS_BOX_LEFT] && (
                              <StepBoxLeft
                                subText={
                                  stepDetails[jsonKeyMapping.STEPS_BOX_LEFT]
                                }
                              />
                            )}
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="row justify-content-md-center">
                          <div className="col-md-12 text-center">
                            {/* Suppress step title for the injected review step */}
                            {stepName !== REVIEW_SLUG && (
                              <h4 className="lg-font blue-color">
                                {stepDetails[jsonKeyMapping.SLUG]}
                              </h4>
                            )}
                          </div>
                        </div>
                        <div className="row justify-content-md-center">
                          {stepDetails[jsonKeyMapping.STEPS_BOX_LEFT] && (
                            <div className="col-md-6 col-lg-4">
                              <div className="steps-box">
                                {stepDetails[jsonKeyMapping.STEPS_BOX_LEFT]}
                              </div>
                            </div>
                          )}
                          {stepDetails[jsonKeyMapping.STEPS_BOX_RIGHT] && (
                            <div className="col-md-6 col-lg-4">
                              <div className="steps-box step-box-right">
                                {stepDetails[jsonKeyMapping.STEPS_BOX_RIGHT]}
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    <div></div>
                  </div>
                </>
              )}

              <div className="profilerightspace h-100">
                <div className="row main-field align-content: baseline">
                  <div className="col-12">
                    <div className="row">
                      <div className="col-12"></div>
                      {/* FORM CONTENT */}
                      <div
                        ref={fieldsScrollRef}
                        className="form-fields-scrollable"
                      >
                        {stepName === REVIEW_SLUG ? (
                          <ReviewSummary templatePrev={templatePrev} />
                        ) : (
                          stepDetails[jsonKeyMapping.FORM_GROUP] &&
                          stepDetails[jsonKeyMapping.FORM_GROUP].length > 0 && (
                            <>
                              <FormBuilder
                                formGroup={
                                  stepDetails[jsonKeyMapping.FORM_GROUP]
                                }
                                groups={stepDetails[jsonKeyMapping.FORM_GROUP]}
                                stepDetails={stepDetails}
                                setActiveFieldIndex={setActiveFieldIndex}
                                activeFieldIndex={activeFieldIndex}
                                singleFieldMode={SINGLE_FIELD_MODE}
                                singleFieldIndex={currentFieldIndex}
                              />
                            </>
                          )
                        )}
                      </div>
                      {/* ACTION BUTTON FOR MOBILE VIEW */}
                      {/* <div className="pb-20 pt-3 bg-grey previewmbdispaly">
                        {((stepDetails[jsonKeyMapping.FORM_GROUP] &&
                          stepDetails[jsonKeyMapping.FORM_GROUP].length > 0) ||
                          stepDetails[jsonKeyMapping.STEPS_BOX_LEFT]) && (
                          <PreviewComponent />
                        )}
                        {stepDetails[jsonKeyMapping.FAQ] &&
                          stepDetails[jsonKeyMapping.FAQ].length > 0 && (
                            <FaqComponent
                              faqContentList={stepDetails[jsonKeyMapping.FAQ]}
                            />
                          )}
                      </div> */}

                      {/* Action Buttons (inside width-constrained container) */}
                      <div
                        id="navigationBar"
                        className="form-navigation-bar-wrapper"
                        ref={actionsRef}
                        data-complete-agreement-button={
                          stepName === REVIEW_SLUG ? true : undefined
                        }
                      >
                        {isLoading ? (
                          <div
                            className="d-flex justify-content-center align-items-center form-navigation-bar loading"
                            aria-label="Loading next step"
                          >
                            <Spinner
                              animation="border"
                              className="spinner"
                              style={{
                                width: "3rem",
                                height: "3rem",
                                textAlign: "center",
                                margin: 10,
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            className="form-navigation-bar d-flex align-items-center"
                          >
                            {stepName === REVIEW_SLUG ? (
                              <div
                                style={{
                                  width: "100%",
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                <ButtonComponent
                                  buttonProps={{
                                    className: "btn primary-button btn-orange complete-agreement-button",
                                    id: "submitBtn",
                                    style: {},
                                    text: "Complete Agreement",
                                  }}
                                  clickHandler={handleSubmitClick}
                                />
                              </div>
                            ) : (
                              <div
                                className={`d-flex flex-grow-1 ${
                                  (hasNext && hasPrevious) ||
                                  (hasSubmit && hasPrevious)
                                    ? "justify-content-between"
                                    : "justify-content-end"
                                } align-items-center`}
                                style={{ width: "100%" }}
                              >
                                {hasPrevious && (
                                  <ButtonComponent
                                    buttonProps={{
                                      className: "btn btn-white",
                                      id: "prevBtn",
                                      style: {},
                                      text: "Back",
                                    }}
                                    clickHandler={handleBack}
                                  />
                                )}
                                {hasNext && (
                                  <ButtonComponent
                                    buttonProps={{
                                      className:
                                        "btn primary-button btn-orange",
                                      id: "nextBtn",
                                      style: {},
                                      text:
                                        SINGLE_FIELD_MODE &&
                                        stepDetails[
                                          jsonKeyMapping.FORM_GROUP
                                        ] &&
                                        currentFieldIndex <
                                          stepDetails[jsonKeyMapping.FORM_GROUP]
                                            .length -
                                            1
                                          ? "Next"
                                          : "Next",
                                    }}
                                    clickHandler={handleNextFieldOrStep}
                                  />
                                )}
                                {hasSubmit && (
                                  <ButtonComponent
                                    buttonProps={{
                                      className:
                                        "btn primary-button btn-orange complete-agreement-button",
                                      id: "submitBtn",
                                      style: {},
                                      text: "Complete Agreement",
                                    }}
                                    clickHandler={handleSubmitClick}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* end inner row containing form fields & buttons */}
                  </div>
                  {/* end col-12 */}
                </div>
                {/* end row main-field */}
              </div>
              {/* end profilerightspace */}
            </div>
            {/* end card-body (was originally inside scrollable-div) */}
          </div>
          {/* end scrollable-div */}
        </div>
        {/* end form-content-container col */}
      </div>
      {/* end row */}
    </>
  );
};

export default MainForm;
